import { useEffect, useRef } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import styled, { css } from 'styled-components'
import getConfig from 'next/config'
import { light } from '../helpers/color'
import { slugify } from '../helpers/string'
import { breakpoint } from '../layout/styles'
import ReactHtmlParser from 'react-html-parser'

// Static base64 grey placeholder used as blur placeholder while images load.
// External CDN URLs are unreliable as blurDataURL with a custom loader.
const BLUR_PLACEHOLDER = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/+F9PQAI8wNPvd7POQAAAABJRU5ErkJggg=='

export const Card = ({ title, image, link, path, color, style, listing, useInfiniteScroll, isLast, setScrollLimit, code, expand_item_images, item_style_override, on_hover_style }: any) => {
  const { publicRuntimeConfig } = getConfig()
  const recipe = path === 'recipes'
  const width = 350
  const height = recipe ? 266 : 0
  const maxChars = publicRuntimeConfig?.alternateProductDetails ? 110 : 60
  const cardRef = useRef(null)
  const imageUrl = image?.url || '/images/blank.svg'
  
  const customLoader = ({ src }: any) => {
    if (src === '/images/blank.svg') return src;
    
    // Syndigo CDN uses size + filetype params; Contentstack (Imgix-based) uses width + auto + format
    if (src.includes('assets.syndigo.com')) {
      return `${src}?size=${width * 2}&filetype=webp`;
    }
    return `${src}?width=${width}${height ? `&height=${height}` : ''}&auto=webp&format=webp&quality=75`;
  }

  // Derived synchronously — no effect needed, eliminates an extra render per card on mount
  const isLoading = !(imageUrl && title)

  useEffect(() => {
    if (!useInfiniteScroll || !cardRef?.current) {
      return
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (isLast && entry.isIntersecting) {
        if (setScrollLimit) {
          setScrollLimit()
        }
        observer.unobserve(entry.target)
      }
    });
  
    observer.observe(cardRef.current);

    return () => {
      observer.disconnect()
    }
  }, [isLast])

  if (publicRuntimeConfig.cardColorOverride && (!color || color === 'transparent')) {
    color = publicRuntimeConfig.cardColorOverride;
  }

  if (isLoading) {
    return null
  }

  return (
    <Wrapper {...{ style, recipe, listing }} ref={cardRef} className={
      `${item_style_override !== undefined ? `style-override-${item_style_override}` : ''} ${on_hover_style !== undefined ? `hover-style-${on_hover_style}` : ''}`
    }>
      <Link href={link} passHref>
        <CardLink bg={color && light(color).replace(';', '')} title={title}>
          <ImageWrapper recipe={recipe} expand_item_images={expand_item_images}>
            <Image
              {...(imageUrl === '/images/blank.svg' ? { unoptimized: true } : { loader: customLoader })}
              src={imageUrl}
              alt=""
              width={width * 2}
              height={recipe ? height * 2 : width * 2}
              loading="lazy"
              placeholder="blur"
              blurDataURL={BLUR_PLACEHOLDER}
            />
          </ImageWrapper>
          <h4>
            <span>{ReactHtmlParser(title?.length > maxChars ? `${title.substr(0, maxChars)}…` : title)}</span>
            {code && <span>{code}</span>}
          </h4>
        </CardLink>
      </Link>
    </Wrapper>
  )
}

export default Card

export const ProductCard = (product: any) => {
  const { publicRuntimeConfig } = getConfig()
  if (!product.url_token) return null
  return (
    <Card
      {...{
        title: product.short_title || product.title || product.gtin,
        link: `/products/${product.filters?.[0]?.value || 'uncategorized'}/${product.url_token}${
          publicRuntimeConfig?.alternateProductDetails
            ? `/${slugify(product.short_title || product.title || product.gtin)}`
            : ``
        }`,
        image: product.images && product.images[0],
        path: 'products',
        color: product.color?.[0]?.background,
        listing: true,
        useInfiniteScroll: product.useInfiniteScroll,
        isLast: product.isLast,
        setScrollLimit: product.setScrollLimit,
        code: product.product_code
      }}
      style={{ '--border-color': product.color?.[0]?.color }}
    />
  )
}
export const RecipeCard = (recipe: any) => {
  const filterValue = recipe.filters?.[0]?.value
  if (!filterValue || !recipe.url_token || !recipe.title) return null
  const link = `/recipes/${filterValue}/${recipe.url_token}`

  return (
    <Card
      {...{
        title: recipe.title.length > 50 ? `${recipe.title.substr(0, 50)}…` : recipe.title,
        link,
        uid: recipe.filters && recipe.filters[0] && `${recipe.filters[0].value}/${recipe.url_token}`,
        image: recipe.images && recipe.images[0],
        path: 'recipes',
        color: recipe.product?.[0]?.color?.[0]?.background,
        listing: true,
        useInfiniteScroll: recipe.useInfiniteScroll,
        isLast: recipe.isLast,
        setScrollLimit: recipe.setScrollLimit
      }}
      style={{ '--border-color': recipe.product?.[0]?.color?.[0]?.color }}
    />
  )
}

export const ImageWrapper = styled.div<{ recipe?: any, expand_item_images?: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  max-height: ${props => props.recipe ? '266px' : '350px'};
  max-width: ${props => props.expand_item_images ? '100%' : props.recipe ? '350px' : '220px'};
  padding: ${props => (props.recipe || props.expand_item_images) ? '0' : '3rem 0'};
  z-index: 1;
  pointer-events: none;
  transition: all ${(props) => (props.recipe ? '3s' : '750ms')} ease;

  img {
    object-fit: ${props => props.recipe ? 'cover' : 'contain'};
  }
`

export const CardLink = styled.a<any>`
  cursor: pointer;
  background: #fff;
  flex: 1 1 auto;
  font-size: 18px;
  line-height: 1.3;
  font-weight: 700;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--text);
  //padding: ${(props) => (props.recipe ? '0 0 2rem' : '3rem 0')};
  @media ${breakpoint.desktop} {
    width: 350px;
  }
  transition: all 450ms ease;
  overflow: hidden;
  position: relative;
  &:after {
    content: '';
    background: ${(props) => props.bg};
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
  }
`

export const Wrapper = styled.div<any>`
  margin-bottom: 1.5rem;
  color: var(--text);
  width: 100%;
  display: flex;
  @media ${breakpoint.desktop} {
    width: auto;
    ${(props) =>
      props.listing &&
      css`
        padding-bottom: 1rem;
      `};
  }
  h3 {
    margin: 0 0 1rem 0;
    font-size: 36px;
    line-height: 46px;
    font-weight: 700;
    max-width: 50%;
  }
  h4 {
    margin: 0;
    padding: ${(props) => (props.recipe ? '2rem 3rem' : '0 3rem 2rem')};
    text-align: center;
    font-size: 23px;
    line-height: 33.8px;
    font-weight: 700;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    position: relative;
    z-index: 1;
    pointer-events: none;

    sup {
      font-size: 12px;
    }
  }
  p {
    font-size: 1rem;
    line-height: 26px;
    margin: 0 0 3rem 0;
    font-weight: 400;
  }

  @media ${breakpoint.desktop} {
    &:hover {
      a {
        transform: translateY(-20px);
        box-shadow: 0px 4px 40px rgba(153, 153, 153, 0.4);
      }
      
      ${ImageWrapper} {
        transform: scale(1.05);
      }
    }
  }
`

export const MainCard = styled(Wrapper)`
  &&& {
    background: var(--primary);
    ${props => props.background_color && css`
      background: ${props.background_color} !important;
    `}
    ${props => props.text_color && css`
      color: ${props.text_color} !important;
      a {
        color: ${props.text_color} !important;
      }
    `}
    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex-direction: column;
    margin-top: 5rem;
    padding: 18px;
    @media ${breakpoint.tablet} {
      width: 345px;
    }
    a {
      height: auto;
      width: auto;
      padding-top: 0;
      padding-right: 3rem;
      padding-bottom: 0;
      flex: inherit;
      white-space: nowrap;
    }
    &:hover {
      transform: none;
      box-shadow: none;
      a {
        transform: none;
        box-shadow: none;
      }
    }
    --text: #fff;
  }
`
